import React from 'react'
import PropTypes from 'prop-types'
import { Card, FlowGrid } from '@fs/zion-ui'
import ReviewCardHeader from './components/ReviewCardHeader'
import ReviewCardBody from './components/ReviewCardBody'
import ReviewCardFooter from './components/ReviewCardFooter'

/**
 * ReviewCard
 *
 * The Review Card is the final step on the Add Name InfoSheet/Panel. It prompts the user to verify
 * entered information and guides them to attach the record, add another person, or close.
 *
 * Display Modes:
 *  - Pending (active=false): Shows only header with gray icon and border card.
 *  - Active (active=true): Shows header + body + footer with gray icon and elevated card.
 *
 * @component
 * @param {Object}   props
 * @param {boolean}  [props.active=true]          Controls visibility of body and footer. If false, only shows header.
 * @param {Object}   props.person                 SLS person object containing at least localizedName.
 * @param {Function} props.onSave                 Called when the user clicks a save button, with 'close' or 'attach'.
 * @param {Function} [props.onAddAnotherPerson]   Called when the user clicks "Add Household Member".
 * @param {string}   [props.splitScreenPartitionKey] PartitionKey of split screen image if active.
 * @param {boolean}  [props.closeOnly=false]      Offers Close alone, for a panel whose caller owns what happens next.
 *
 * @returns {JSX.Element}
 */
export const ReviewCard = ({
  active = true,
  person,
  onSave,
  onAddAnotherPerson,
  splitScreenPartitionKey,
  closeOnly = false,
}) => {
  if (!onSave) {
    console.error('ReviewCard: onSave prop is required but was not provided')
    return null
  }

  return (
    <Card margins="xxs" edges={active ? 'elevation' : 'border'}>
      <FlowGrid orientation="vertical" columnCount={1} gutters="xs">
        <ReviewCardHeader active={active} />
        {active && (
          <ReviewCardBody person={person} splitScreenPartitionKey={splitScreenPartitionKey} closeOnly={closeOnly} />
        )}
        {active && <ReviewCardFooter onSave={onSave} onAddAnotherPerson={onAddAnotherPerson} closeOnly={closeOnly} />}
      </FlowGrid>
    </Card>
  )
}

ReviewCard.propTypes = {
  active: PropTypes.bool,
  person: PropTypes.shape({
    localizedName: PropTypes.string,
  }).isRequired,
  onSave: PropTypes.func.isRequired,
  // Not required: under closeOnly the button that calls it is never rendered.
  onAddAnotherPerson: PropTypes.func,
  splitScreenPartitionKey: PropTypes.string,
  closeOnly: PropTypes.bool,
}

ReviewCard.defaultProps = {
  active: true,
  closeOnly: false,
}

export default ReviewCard
